import {
  CurrentAccount,
  IdentityProviderCapability,
} from 'types/CurrentAccount';
import {
  AccountFeatureFlagName,
  AccountFeatureFlagState,
  AccountFeatureFlagMap,
} from 'types/AccountFeatureFlags';
import { useCanOverrideFeatures } from 'features/featureFeature/useCanOverrideFeatures';
import { useFeatureOverrideStorage } from 'features/featureFeature/useFeatureOverrideStorage';

import {
  accessAccountFeatureFlagMap,
  accessDefaultConfidenceLevel,
  Awaitable,
} from './util';
import { useCurrentAccount } from './baseState/useCurrentAccount';

type AwaitableAccountFeatureFlagState = Awaitable<AccountFeatureFlagState>;

const useCurrentAccountAwaitableWithTransform: <TData>(
  transform: (t: CurrentAccount) => TData
) => Awaitable<TData> = transform => {
  const currentAccount = useCurrentAccount();
  const canOverride = useCanOverrideFeatures();
  const { overrideAccount } = useFeatureOverrideStorage();
  if (currentAccount) {
    return {
      loading: false,
      data: transform(
        canOverride ? overrideAccount(currentAccount) : currentAccount
      ),
    };
  }
  return { loading: true };
};

export const useGetAccountFeatureStatus = (
  featureName: AccountFeatureFlagName
): AwaitableAccountFeatureFlagState =>
  useCurrentAccountAwaitableWithTransform(
    account => accessAccountFeatureFlagMap(account)[featureName]
  );

export const useHasAccountFeatureFlagActive = (
  featureName: AccountFeatureFlagName
): boolean => {
  const awaitable = useGetAccountFeatureStatus(featureName);
  return awaitable.loading === false && awaitable.data === 'active';
};

export const useIsManagedByIdentityProvider = (
  capability: IdentityProviderCapability
): boolean => {
  const awaitable = useCurrentAccountAwaitableWithTransform(
    account => account.identityProviderManaged
  );
  return awaitable.loading === false && awaitable.data.includes(capability);
};

export const useHasAccountFeatureFlagInactive = (
  featureName: AccountFeatureFlagName
): boolean => {
  const awaitable = useGetAccountFeatureStatus(featureName);
  return awaitable.loading === false && awaitable.data === 'inactive';
};

export const useAccountFeatureFlagsAwaitable =
  (): Awaitable<AccountFeatureFlagMap> => {
    return useCurrentAccountAwaitableWithTransform(account =>
      accessAccountFeatureFlagMap(account)
    );
  };

export const useAccountTranslationsAwaitable = (): Awaitable<
  CurrentAccount['translations']
> => {
  return useCurrentAccountAwaitableWithTransform(
    account => account.translations
  );
};

export const useAccountTranslations = (): CurrentAccount['translations'] => {
  const awaitable = useAccountTranslationsAwaitable();
  return awaitable.loading === false ? awaitable.data : {};
};

export const useAccountTranslationVariant =
  (): CurrentAccount['translationVariant'] => {
    const awaitable = useCurrentAccountAwaitableWithTransform(
      account => account.translationVariant
    );
    return awaitable.loading === false ? awaitable.data : '';
  };

export const useDefaultConfidenceLevel = (): number =>
  accessDefaultConfidenceLevel(useCurrentAccount());
